import { apiFetch } from "@/lib/api";
import type {
  ApiMessage,
  Conversation,
  ConversationKind,
  Member,
  MemberRole,
  Message,
  UnreadSnapshot,
} from "@/features/chat/types";

/**
 * The Django API wraps every body in the standard envelope
 * (`APIJSONRenderer`): `{ success, message, data }`, plus `meta` for paginated
 * lists. `apiFetch` returns that envelope verbatim, so this layer unwraps
 * `data`/`meta` and maps the snake_case read shapes onto the camelCase types.
 */
interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
  meta?: { count: number; next: string | null; previous: string | null };
}

interface ApiMember {
  id: string;
  name: string;
  email: string;
  role?: MemberRole;
}

interface ApiConversation {
  id: string;
  kind: ConversationKind;
  name: string;
  members: ApiMember[];
  unread_count: number;
  last_message: { body: string; sender_name: string; at: string | null } | null;
  last_message_at: string | null;
  created_at: string;
  updated_at: string;
}

function toMember(m: ApiMember): Member {
  return { id: m.id, name: m.name, email: m.email, role: m.role ?? "member" };
}

function toConversation(c: ApiConversation): Conversation {
  return {
    id: c.id,
    kind: c.kind,
    name: c.name,
    members: c.members.map(toMember),
    unreadCount: c.unread_count,
    lastMessage: c.last_message
      ? { body: c.last_message.body, senderName: c.last_message.sender_name, at: c.last_message.at }
      : null,
    lastMessageAt: c.last_message_at,
    createdAt: c.created_at,
    updatedAt: c.updated_at,
  };
}

export function toMessage(m: ApiMessage): Message {
  return {
    id: m.id,
    conversation: m.conversation,
    sender: m.sender,
    senderName: m.sender_name,
    body: m.body,
    isDeleted: m.is_deleted ?? false,
    editedAt: m.edited_at ?? null,
    createdAt: m.created_at,
  };
}

export async function listConversations(): Promise<Conversation[]> {
  const res = await apiFetch<Envelope<ApiConversation[]>>("/chat/conversations/");
  return res.data.map(toConversation);
}

export interface CreateConversationPayload {
  kind: ConversationKind;
  name?: string;
  memberIds: string[];
}

export async function createConversation(
  payload: CreateConversationPayload,
): Promise<Conversation> {
  const res = await apiFetch<Envelope<ApiConversation>>("/chat/conversations/", {
    method: "POST",
    json: {
      kind: payload.kind,
      name: payload.name ?? "",
      member_ids: payload.memberIds,
    },
  });
  return toConversation(res.data);
}

export interface MessagePage {
  messages: Message[];
  /** Next page number for older messages, or null when exhausted. */
  nextPage: number | null;
}

export async function listMessages(
  conversationId: string,
  page = 1,
): Promise<MessagePage> {
  const res = await apiFetch<Envelope<ApiMessage[]>>(
    `/chat/conversations/${conversationId}/messages/?page=${page}`,
  );
  // The endpoint returns newest-first; the thread renders oldest-first, so
  // reverse each page for display.
  return {
    messages: res.data.map(toMessage).reverse(),
    nextPage: res.meta?.next ? page + 1 : null,
  };
}

export async function sendMessage(conversationId: string, body: string): Promise<Message> {
  const res = await apiFetch<Envelope<ApiMessage>>(
    `/chat/conversations/${conversationId}/messages/`,
    { method: "POST", json: { body } },
  );
  return toMessage(res.data);
}

export async function markConversationRead(conversationId: string): Promise<UnreadSnapshot> {
  const res = await apiFetch<Envelope<{ total: number; per_conversation: Record<string, number> }>>(
    `/chat/conversations/${conversationId}/mark-read/`,
    { method: "POST" },
  );
  return { total: res.data.total, perConversation: res.data.per_conversation };
}

export async function getUnread(): Promise<UnreadSnapshot> {
  const res = await apiFetch<Envelope<{ total: number; per_conversation: Record<string, number> }>>(
    "/chat/conversations/unread/",
  );
  return { total: res.data.total, perConversation: res.data.per_conversation };
}

export async function searchMembers(q = ""): Promise<Member[]> {
  const query = q ? `?q=${encodeURIComponent(q)}` : "";
  const res = await apiFetch<Envelope<ApiMember[]>>(`/chat/conversations/members/${query}`);
  return res.data.map(toMember);
}

export async function getPresence(): Promise<string[]> {
  const res = await apiFetch<Envelope<{ online_user_ids: string[] }>>(
    "/chat/conversations/presence/",
  );
  return res.data.online_user_ids;
}

export async function editMessage(
  conversationId: string,
  messageId: string,
  body: string,
): Promise<Message> {
  const res = await apiFetch<Envelope<ApiMessage>>(
    `/chat/conversations/${conversationId}/messages/${messageId}/`,
    { method: "PATCH", json: { body } },
  );
  return toMessage(res.data);
}

export async function deleteMessage(conversationId: string, messageId: string): Promise<void> {
  await apiFetch(`/chat/conversations/${conversationId}/messages/${messageId}/`, {
    method: "DELETE",
  });
}

export async function leaveConversation(conversationId: string): Promise<void> {
  await apiFetch(`/chat/conversations/${conversationId}/leave/`, { method: "POST" });
}

export async function deleteConversation(conversationId: string): Promise<void> {
  await apiFetch(`/chat/conversations/${conversationId}/`, { method: "DELETE" });
}

export async function renameConversation(
  conversationId: string,
  name: string,
): Promise<Conversation> {
  const res = await apiFetch<Envelope<ApiConversation>>(
    `/chat/conversations/${conversationId}/`,
    { method: "PATCH", json: { name } },
  );
  return toConversation(res.data);
}

export async function addMembers(
  conversationId: string,
  memberIds: string[],
): Promise<Conversation> {
  const res = await apiFetch<Envelope<ApiConversation>>(
    `/chat/conversations/${conversationId}/members/`,
    { method: "POST", json: { member_ids: memberIds } },
  );
  return toConversation(res.data);
}

export async function removeMember(
  conversationId: string,
  userId: string,
): Promise<Conversation> {
  const res = await apiFetch<Envelope<ApiConversation>>(
    `/chat/conversations/${conversationId}/remove-member/`,
    { method: "POST", json: { user_id: userId } },
  );
  return toConversation(res.data);
}

export async function setMemberRole(
  conversationId: string,
  userId: string,
  role: MemberRole,
): Promise<Conversation> {
  const res = await apiFetch<Envelope<ApiConversation>>(
    `/chat/conversations/${conversationId}/set-role/`,
    { method: "POST", json: { user_id: userId, role } },
  );
  return toConversation(res.data);
}
